import { useEffect, useState } from 'react';
import { FieldConfig } from '../../../../../components/CustomFormsComponents/CustomField';
import { TEST_IDS } from '../testIds';
import { useAppSelector } from '../../../../../hooks/reduxHooks';
import { ValidationName } from '../../../../../components/CustomFormsComponents/hooks/useValidation';
const IONIZING_RADIATION_RISK_CODE = '90002'
const USAGE_INDUSTRIAL_COMPONENT_ID = 7
const USAGE_MEDICAL_EQUIPMENT_ID = 8
const USAGE_AMBIENTAL_ID = 9

export const SPECIAL_USAGES_IDS = [USAGE_INDUSTRIAL_COMPONENT_ID, USAGE_MEDICAL_EQUIPMENT_ID, USAGE_AMBIENTAL_ID];


export const BOOLEAN_OPTIONS = [
    { id: 1, value: true, nombre: 'Si' },
    { id: 0, value: false, nombre: 'No' }
];

const useEditSubstanceFieldsConfig = ({ t, editSubstanceFormData, selectedSubstance, handleBlurValidation, noComercialNameChecked }: any) => {
    const [editSubstanceFieldsConfig, setEditSubstanceFieldsConfig] = useState<FieldConfig[]>([]);
    const substanceRiskCode = selectedSubstance?.codigo;

    const quantityAndMeasureRequired = () => {
        const item = editSubstanceFormData?.usageOrigin?.find(
            (usage: { id: number; nombre: string }) => !SPECIAL_USAGES_IDS.includes(usage.id)
        );
        return item ? true : false;
    }
    
    const carcinogenicParametricData = useAppSelector(
        (state) => state.carcinogenicReport.carcinogenicParametricData
    );
    useEffect(() => {
        const editSubstanceFieldsConfig: FieldConfig[] = [
            {
                type: 'text',
                label: t('editSubstanceForm.inputs.substance') as string,
                name: 'substance',
                required: true,
                value: editSubstanceFormData.substance,
                disabled: true,
                helperText: t('editSubstanceForm.inputs.substance') as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12 },
                testID: TEST_IDS.editSubstanceFields.substance,
                validations: [{ name: 'required' }]
            },
            {
                type: 'text',
                label: t('editSubstanceForm.inputs.workPositionAndSector') as string,
                name: 'workPositionAndSector',
                required: true,
                value: editSubstanceFormData.workPositionAndSector,
                disabled: true,
                helperText: t('editSubstanceForm.inputs.workPositionAndSector') as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12 },
                testID: TEST_IDS.editSubstanceFields.workPositionAndSector,
                validations: [{ name: 'required' }]
            },
            {
                type: 'text',
                label: t('editSubstanceForm.inputs.comercialName') as string,
                name: 'comercialName',
                required: true,
                value: editSubstanceFormData.comercialName,
                disabled: noComercialNameChecked,
                helperText: t('editSubstanceForm.inputs.comercialName') as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12 },
                testID: TEST_IDS.editSubstanceFields.comercialName,
                validations: [{ name: 'required' }, { name: 'maxLength', param: 150 }]
            },
            {
                type: 'autocomplete-default',
                label: t('editSubstanceForm.inputs.usageOrigin') as string,
                name: 'usageOrigin',
                required: true,
                value: editSubstanceFormData.usageOrigin,
                disabled: false,
                multiple: true,
                helperText: t('editSubstanceForm.inputs.usageOrigin') as string,
                onBlurValidation: handleBlurValidation,
                options: carcinogenicParametricData?.usos || [],
                getOptionLabel: (item) => `${item?.descripcion}`,
                gridSpace: { xs: 12 },
                testID: TEST_IDS.editSubstanceFields.usageOrigin,
                validations: [{ name: 'required' }]
            },
            {
                type: 'autocomplete-default',
                label: t('editSubstanceForm.inputs.employmentMethods') as string,
                name: 'employmentMethods',
                required: true,
                value: editSubstanceFormData.employmentMethods,
                disabled: false,
                multiple: true,
                helperText: t('editSubstanceForm.inputs.employmentMethods') as string,
                onBlurValidation: handleBlurValidation,
                inputType: 'text',
                options: carcinogenicParametricData?.modosEmpleo || [],
                getOptionLabel: (item) => `${item?.descripcion}`,
                gridSpace: { xs: 12 },
                testID: TEST_IDS.editSubstanceFields.employmentMethods,
                validations: [{ name: 'required' }, { name: 'maxLength', param: 50 }]
            },
            {
                type: 'text',
                label: t('editSubstanceForm.inputs.substanceAnualQuantity') as string,
                name: 'substanceAnualQuantity',
                required: quantityAndMeasureRequired(),
                value: editSubstanceFormData.substanceAnualQuantity,
                disabled: false,
                helperText: t('editSubstanceForm.inputs.substanceAnualQuantity') as string,
                onBlurValidation: handleBlurValidation,
                inputType: 'number',
                maxLength: 30,
                gridSpace: { xs: 6 },
                testID: TEST_IDS.editSubstanceFields.substanceAnualQuantity,
                validations: quantityAndMeasureRequired()
                    ? [
                          { name: 'required' },
                          { name: 'maxLength', param: 30 },
                          { name: 'minValue', param: 1 }
                      ]
                    : editSubstanceFormData?.substanceAnualQuantity && [
                          { name: 'maxLength', param: 30 },
                          { name: 'minValue', param: 1 }
                      ]
            },
            {
                type: 'autocomplete',
                label: t('editSubstanceForm.inputs.measureUnit') as string,
                name: 'measureUnit',
                required: editSubstanceFormData?.substanceAnualQuantity,
                value: editSubstanceFormData.measureUnit,
                disabled: !editSubstanceFormData?.substanceAnualQuantity,
                helperText: t('editSubstanceForm.inputs.measureUnit') as string,
                onBlurValidation: handleBlurValidation,
                options: carcinogenicParametricData?.unidadesMedida || [],
                getOptionLabel: (item) => `${item?.descripcion}`,
                gridSpace: { xs: 6 },
                testID: TEST_IDS.editSubstanceFields.measureUnit,
                validations: [
                    ...(editSubstanceFormData.substanceAnualQuantity
                        ? [{ name: 'required' as ValidationName }]
                        : [])
                ]
            },
            {
                type: 'autocomplete-default',
                label: t('editSubstanceForm.inputs.report') as string,
                name: 'report',
                required: true,
                value: editSubstanceFormData.report,
                disabled: false,
                helperText: t('editSubstanceForm.inputs.report') as string,
                onBlurValidation: handleBlurValidation,
                options: BOOLEAN_OPTIONS,
                gridSpace: { xs: 6 },
                testID: TEST_IDS.editSubstanceFields.report,
                validations: [{ name: 'required' }, { name: 'maxLength', param: 50 }]
            },
            {
                type: 'autocomplete-default',
                label: t('editSubstanceForm.inputs.trainingProgram') as string,
                name: 'trainingProgram',
                required: true,
                value: editSubstanceFormData.trainingProgram,
                disabled: false,
                helperText: t('editSubstanceForm.inputs.trainingProgram') as string,
                onBlurValidation: handleBlurValidation,
                options: BOOLEAN_OPTIONS,
                gridSpace: { xs: 6 },
                testID: TEST_IDS.editSubstanceFields.trainingProgram,
                validations: [{ name: 'required' }, { name: 'maxLength', param: 50 }]
            },
            {
                type: 'text',
                label: t('editSubstanceForm.inputs.studiesAndReplacementPosibility') as string,
                name: 'studiesAndReplacementPosibility',
                required: true,
                value: editSubstanceFormData.studiesAndReplacementPosibility,
                disabled: false,
                helperText: t('editSubstanceForm.inputs.studiesAndReplacementPosibility') as string,
                onBlurValidation: handleBlurValidation,
                inputType: 'text',
                gridSpace: { xs: 12 },
                testID: TEST_IDS.editSubstanceFields.studiesAndReplacementPosibility,
                validations: [{ name: 'required' }, { name: 'maxLength', param: 50 }]
            },
            {
                type: 'autocomplete-default',
                label: t('editSubstanceForm.inputs.specialLicense') as string,
                name: 'specialLicense',
                required: substanceRiskCode === IONIZING_RADIATION_RISK_CODE,
                value: editSubstanceFormData.specialLicense,
                disabled: !(substanceRiskCode === IONIZING_RADIATION_RISK_CODE),
                helperText: t('editSubstanceForm.inputs.specialLicense') as string,
                onBlurValidation: handleBlurValidation,
                options: BOOLEAN_OPTIONS,
                gridSpace: { xs: 12 },
                testID: TEST_IDS.editSubstanceFields.specialLicense,
                validations:
                    substanceRiskCode === IONIZING_RADIATION_RISK_CODE ? [{ name: 'required' }] : []
            },
            {
                type: 'autocomplete-default',
                label: t('editSubstanceForm.inputs.preventiveMeasures') as string,
                name: 'preventiveMeasures',
                required: true,
                value: editSubstanceFormData.preventiveMeasures,
                disabled: false,
                multiple: true,
                helperText: t('editSubstanceForm.inputs.preventiveMeasures') as string,
                onBlurValidation: handleBlurValidation,
                options: carcinogenicParametricData?.medidasPrevencion || [],
                getOptionLabel: (item) => `${item?.descripcion}`,
                gridSpace: { xs: 12 },
                testID: TEST_IDS.editSubstanceFields.preventiveMeasures,
                validations: [{ name: 'required' }]
            },
            {
                type: 'autocomplete-default',
                label: t('editSubstanceForm.inputs.protectiveEquipment') as string,
                name: 'protectiveEquipment',
                required: true,
                value: editSubstanceFormData.protectiveEquipment,
                disabled: false,
                multiple: true,
                helperText: t('editSubstanceForm.inputs.protectiveEquipment') as string,
                onBlurValidation: handleBlurValidation,
                options: carcinogenicParametricData?.elementosProteccion || [],
                getOptionLabel: (item) => `${item?.descripcion}`,
                gridSpace: { xs: 12 },
                testID: TEST_IDS.editSubstanceFields.protectiveEquipment,
                validations: [{ name: 'required' }]
            }
        ];

        setEditSubstanceFieldsConfig(editSubstanceFieldsConfig);
    }, [
        t,
        substanceRiskCode,
        editSubstanceFormData?.substanceAnualQuantity,
        editSubstanceFormData.usageOrigin,
        noComercialNameChecked
    ]);

    return { editSubstanceFieldsConfig };
};

export default useEditSubstanceFieldsConfig;
